import { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { 
  CheckCircle, 
  AlertCircle, 
  Copy, 
  RefreshCw, 
  Trash2,
  Plug,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsPage() {
  const { getToken } = useTenant();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/integrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch integrations');

      const data = await response.json();
      setIntegrations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWebhook = async (provider) => {
    if (!window.confirm("Generate a new webhook URL? This will replace any existing one.")) return;
    
    setActionLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/integrations/${provider}/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to generate webhook');
      }

      await fetchIntegrations();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm("Are you sure? This will stop ROI tracking for this integration.")) return;

    setActionLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/integrations/${provider}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to disconnect');
      }

      await fetchIntegrations();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const [copied, setCopied] = useState(false);
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        <div><Skeleton className="h-8 w-48 mb-2" /><Skeleton className="h-4 w-72" /></div>
        <div className="grid md:grid-cols-2 gap-6">
          {[1,2].map(i => <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 space-y-4"><div className="flex items-center gap-3"><Skeleton className="h-10 w-10 rounded-lg" /><Skeleton className="h-5 w-32" /></div><Skeleton className="h-4 w-full" /><Skeleton className="h-10 w-28" /></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Plug className="w-6 h-6 text-orange-500" />
          Integrations
        </h1>
        <p className="text-gray-600 mt-2 max-w-2xl">
          Connect DAZZA to your existing ordering or POS systems so we can automatically receive orders 
          and measure the ROI of your campaigns.
        </p>
        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>
            This page is only for ROI measurement. It is not for triggering campaigns or sending SMS.
            <br/>
            <strong>Core Principle:</strong> ROI is measured only when a promo code sent via SMS is used in an online order.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {integrations.map((integration) => {
          const isConnected = integration.status === 'active';
          
          return (
            <div 
              key={integration.id} 
              className={`bg-white rounded-xl shadow-sm border p-6 transition-all ${
                isConnected ? 'border-green-200 ring-1 ring-green-100' : 'border-gray-200'
              }`}
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    isConnected ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {/* Placeholder Logo */}
                    <span className="font-bold text-lg">ON</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{integration.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {isConnected ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3" />
                          Connected — receiving orders
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          <Loader2 className="w-3 h-3" />
                          Waiting for first order
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Webhook Configuration */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={integration.webhook_url || ''}
                      className="flex-1 bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-orange-500 focus:border-orange-500 block w-full p-2.5 font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(integration.webhook_url)}
                      className={`p-2.5 rounded-lg border border-gray-200 ${copied ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                      title={copied ? 'Copied!' : 'Copy URL'}
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Paste this URL into your {integration.name} webhook settings.
                  </p>
                </div>

                {isConnected && integration.last_sync_at && (
                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    Last order received: {new Date(integration.last_sync_at).toLocaleString()}
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                  {!isConnected ? (
                    <button
                      onClick={() => handleGenerateWebhook(integration.provider)}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
                      Regenerate Webhook
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDisconnect(integration.provider)}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Disconnect
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
